// Office contracts page. Lists every contract with its tier, annual value, status,
// renewal date and escalator, flagging those renewing within 90 days. Each row can
// generate a renewal letter (AI-drafted, editable) that is saved to Gmail drafts
// for review -- never sent automatically. The Renewals Due dashboard tile links
// here with ?filter=due-90-days.
import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ApiError, api } from "../../lib/api";
import type { ContractListItem, RenewalDraft } from "../../lib/api";
import { friendlyApiError } from "../../lib/friendlyApiError";
import { ConfirmDeleteModal } from "../../components/ConfirmDeleteModal";
import { useSortableColumn } from "../../lib/useSortableColumn";
import { useServerPage } from "../../lib/useServerPage";
import { Pager } from "../../components/Pager";
import { SortableHeader } from "../../components/SortableHeader";
import { MODAL_ACTION_BTN, ModalShell } from "../../components/ModalShell";
import { useCustomerFilter, CustomerFilterChip } from "./customerFilter";
import { initialMobileLayout } from "../../lib/mobileLayout";
import { MobileCardList, MobileListCard } from "../../components/MobileListCard";
import { PageHeader } from "../../components/PageHeader";
import { ExportCsvMenu } from "../../components/ExportCsvMenu";
import { Button } from "../../components/Button";
import { Loader } from "../../components/Loader";
import { EmptyState } from "../../components/EmptyState";
import { StatusPill } from "../../components/StatusPill";
import { formatDate } from "../../lib/format";

const TIER_LABEL: Record<string, string> = { core: "Core", care: "Care", complete: "Complete" };
const FREQ_LABEL: Record<string, string> = {
  monthly: "Monthly",
  bi_monthly: "Every 2 months",
  quarterly: "Quarterly",
  six_monthly: "Twice yearly",
  annual: "Annual",
};
// Session SC2C: the list is 50 at a time and the SERVER filters, sorts and counts it (GET /api/contracts, contractListQuery.ts). The sort
// columns are customer, site, tier, annual, billing, status, renewal and escalator.
const CONTRACT_PAGE_SIZE = 50;

function gbp(n: number): string {
  return `£${Math.round(n).toLocaleString("en-GB")}`;
}

// Session AMB. renewalDueSoon (server, contracts.ts) only ever looks FORWARD (today through +90
// days), so once a renewal date slips into the past it silently stops being flagged at all -- an
// active contract renewing today reads "Due soon"; the same contract renewing a week ago reads
// identically to one renewing next year. Found live on 21 Rosina Street: annualValue already showed
// the post rise figure (a CPI confirm had been actioned) while renewalDate still sat in the past with
// no on-screen signal that anything was different from a healthy contract. Complementary to
// renewalDueSoon (today's UTC midnight is the shared boundary: on/after it is due soon, before it is
// overdue), so the two never show together and never leave a gap between them. Computed purely from
// fields the list already carries, client-side only: no server or MCP surface changed by this.
function isRenewalOverdue(c: Pick<ContractListItem, "status" | "renewalDate">): boolean {
  if (c.status !== "active" || !c.renewalDate) return false;
  const anchor = new Date(c.renewalDate);
  if (Number.isNaN(anchor.getTime())) return false;
  const today = new Date();
  const startOfToday = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate());
  return anchor.getTime() < startOfToday;
}

export function ContractsPage() {
  const navigate = useNavigate();
  // Card list below md, table otherwise -- decided once at mount (Session AHB).
  const [mobileCards] = useState(initialMobileLayout);
  const [renewFor, setRenewFor] = useState<ContractListItem | null>(null);
  // Delete: the contract pending a type-to-confirm delete, plus the in-flight flag and the
  // server's 409 blocker message.
  const [deleteFor, setDeleteFor] = useState<ContractListItem | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  // Session ADI: when linked from the customer detail page with ?customerId=, show only that
  // customer's contracts (composes with the due-90-days filter below).
  const { customerId, clear } = useCustomerFilter();

  const dueOnly = searchParams.get("filter") === "due-90-days";
  // Session ARY, Item 4: the Xero billing tile links here with ?filter=xero-billing.
  const xeroBillingOnly = searchParams.get("filter") === "xero-billing";

  async function confirmDelete() {
    if (!deleteFor) return;
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      await api.contracts.remove(deleteFor.id);
      setDeleteFor(null);
      await page.reload();
    } catch (err) {
      // The 409 carries a plain-English blocker message (ApiError.code).
      setDeleteError(friendlyApiError(err, "Could not delete the contract. Please try again."));
    } finally {
      setDeleteBusy(false);
    }
  }

  // Session SC2C: the filter (the due-in-90-days or Xero billing views), the customer link and the sort are asked of the SERVER, which answers
  // one page of 50, the TRUE total and the counts this page prints over the whole book. The Xero billing verdict arrives for the rows shown only.
  const sort = useSortableColumn("contracts");
  const queryKey = JSON.stringify({ dueOnly, xeroBillingOnly, customerId, sk: sort.sortKey, sd: sort.sortDir });
  const page = useServerPage(queryKey, (at) =>
    api.contracts.listPage({
      ...(dueOnly ? { filter: "due-90-days" as const } : xeroBillingOnly ? { filter: "xero-billing" as const } : {}),
      ...(customerId ? { customerId } : {}),
      ...(sort.sortKey ? { sort: sort.sortKey, dir: sort.sortDir } : {}),
      limit: CONTRACT_PAGE_SIZE,
      offset: at,
    }),
  );
  const loading = page.loading;
  const error = page.error;
  const shown: ContractListItem[] = page.data?.items ?? [];
  const sorted = shown;
  const total = page.data?.total ?? 0;
  const dueSoonCount = page.data?.counts.dueSoon ?? 0;
  // Session ARY, Item 4: the two counts the banner below names, over the WHOLE book (never just the rows shown), so the banner still reads
  // correctly however the office has sorted. Only the Xero billing view carries them.
  const xeroWarningCount = page.data?.counts.xeroWarning ?? 0;
  const xeroInfoCount = page.data?.counts.xeroInfo ?? 0;

  function clearFilter() {
    const next = new URLSearchParams(searchParams);
    next.delete("filter");
    setSearchParams(next, { replace: true });
  }

  return (
    <div>
      <PageHeader
        className="mb-4"
        title="Contracts"
        subtitle={`Every contract on the books. ${
          dueSoonCount > 0 ? `${dueSoonCount} renewing within 90 days (flagged below).` : "None are renewing within the next 90 days."
        }`}
        actions={
          // Session GAP9: the CSV of this list as it is showing (the same view, customer and sort), in the More actions menu.
          <ExportCsvMenu
            list="contracts"
            params={{
              ...(dueOnly ? { filter: "due-90-days" } : xeroBillingOnly ? { filter: "xero-billing" } : {}),
              ...(customerId ? { customerId } : {}),
              ...(sort.sortKey ? { sort: sort.sortKey, dir: sort.sortDir } : {}),
            }}
          />
        }
      />

      {dueOnly && (
        // Session ACW: flex-wrap so the "Clear filter" link drops to its own line rather than
        // overflowing the banner on a narrow phone.
        <div className="mb-4 flex flex-wrap items-center gap-2 rounded-ele border border-ele-blue bg-ele-surface px-3 py-1.5 text-xs text-ele-blue">
          Showing contracts renewing within 90 days
          <button onClick={clearFilter} className="text-ele-green hover:underline">Clear filter</button>
        </div>
      )}

      {xeroBillingOnly && (
        // Session ARY, Item 4: the Xero billing tile's own filter, same banner idiom as due-90-days
        // above. The counts are over the whole book, not just what a column sort currently shows.
        <div className="mb-4 flex flex-wrap items-center gap-2 rounded-ele border border-ele-blue bg-ele-surface px-3 py-1.5 text-xs text-ele-blue">
          Showing contracts whose Xero billing needs a look: {xeroWarningCount} out of step or not checked, {xeroInfoCount} with no template
          linked.
          <button onClick={clearFilter} className="text-ele-green hover:underline">Clear filter</button>
        </div>
      )}

      {customerId && (
        <div className="mb-4">
          <CustomerFilterChip noun="contracts" onClear={clear} />
        </div>
      )}

      {loading ? (
        <Loader />
      ) : error ? (
        <p role="alert" className="rounded-ele border border-ele-purple-accent bg-ele-surface p-6 text-center text-ele-strong">
          Could not load the contracts. Please try again in a moment.
        </p>
      ) : shown.length === 0 ? (
        dueOnly || xeroBillingOnly || customerId ? (
          <EmptyState role="status" message="No contracts match this filter." />
        ) : (
          <EmptyState message="No contracts yet." />
        )
      ) : mobileCards ? (
        /* Session AHB. Nine desktop columns collapse to three below md, and those three then
           divided 311px between them: the customer had 116px, the annual value 66px and the
           status 56px against 120px of ink. The card gives the tier and the renewal date back
           too, both of which were desktop-only. The Generate/Delete pair stays desktop-only,
           as it already was: the card taps through to the contract detail page, which carries
           both (the Session ACW decision, unchanged). */
        <MobileCardList label="Contracts">
          {sorted.map((c) => (
            <MobileListCard
              key={c.id}
              to={`/office/contracts/${c.id}`}
              primary={c.customerName}
              secondary={c.siteName}
              meta={
                c.renewalDate
                  ? `Renews ${formatDate(c.renewalDate)}`
                  : c.status === "pending_signature" || c.status === "draft"
                    ? "Pending commencement"
                    : undefined
              }
              right={
                <>
                  <span className="font-semibold text-ele-strong">{gbp(c.annualValue)}</span>
                  <StatusPill domain="contract" status={c.status} />
                  <StatusPill word={TIER_LABEL[c.tier] ?? c.tier} tone="purple" />
                  {c.renewalDueSoon && <StatusPill word="Due soon" tone="amber" />}
                  {isRenewalOverdue(c) && <StatusPill word="Overdue" tone="red" />}
                </>
              }
            />
          ))}
        </MobileCardList>
      ) : (
        // Fixed-layout list table (Session AJ, Item 4): the table fits the available width and
        // truncates over-long text with an ellipsis instead of forcing a horizontal scrollbar.
        <div className="overflow-hidden rounded-ele border border-ele-purple-accent">
          <table className="ele-list-table w-full text-left text-sm">
            {/* Widths sum to 100. The Escalator column is wide enough for the full heading
                (Session AR Item 5: it was truncating to "ESCALA..." at the old 9%).
                Session AEN: the Actions column was 10%, which is 97px on a 1280px office laptop
                and 33px short of the 131px the Generate + Delete pair actually needs. Because the
                buttons sit in a justify-end flex row and .cell-actions is overflow-visible (so a
                button or badge is never clipped), the shortfall spilled LEFT and painted
                "Generate" over the Escalator value: measured at exactly 9.2px of overlap on every
                row at 1280px, with only 6.8px of clearance even at 1440px. Actions is now 15%
                (146px at 1280px, comfortably clear), funded by one point each from Customer,
                Renewal and Escalator and two from Site, all of which still hold their heading and
                their widest real value. */}
            <colgroup>
              <col style={{ width: "14%" }} />
              <col className="max-md:hidden" style={{ width: "12%" }} />
              <col className="max-md:hidden" style={{ width: "8%" }} />
              <col style={{ width: "9%" }} />
              <col className="max-md:hidden" style={{ width: "10%" }} />
              <col style={{ width: "8%" }} />
              <col className="max-md:hidden" style={{ width: "12%" }} />
              <col className="max-md:hidden" style={{ width: "12%" }} />
              <col className="max-md:hidden" style={{ width: "15%" }} />
              {/* Mobile-only chevron disclosure column (Session ACW): the row itself now navigates
                  to the contract detail page, which carries the same Generate/Delete actions. */}
              <col className="w-8 md:hidden" />
            </colgroup>
            <thead className="bg-ele-inset font-menu text-xs uppercase tracking-widest text-ele-blue">
              <tr>
                <SortableHeader label="Customer" colKey="customer" sort={sort} onToggle={sort.toggle} />
                <SortableHeader label="Site" colKey="site" sort={sort} onToggle={sort.toggle} className="max-md:hidden" />
                <SortableHeader label="Tier" colKey="tier" sort={sort} onToggle={sort.toggle} className="max-md:hidden" />
                <SortableHeader label="Annual" colKey="annual" sort={sort} onToggle={sort.toggle} align="right" />
                <SortableHeader label="Billing" colKey="billing" sort={sort} onToggle={sort.toggle} className="max-md:hidden" />
                <SortableHeader label="Status" colKey="status" sort={sort} onToggle={sort.toggle} />
                <SortableHeader label="Renewal" colKey="renewal" sort={sort} onToggle={sort.toggle} className="max-md:hidden" />
                <SortableHeader label="Escalator" colKey="escalator" sort={sort} onToggle={sort.toggle} align="right" className="max-md:hidden" />
                <th className="cell-actions px-3 py-2 text-right max-md:hidden">Actions</th>
                <th className="w-8 cell-actions md:hidden" aria-hidden="true" />
              </tr>
            </thead>
            <tbody>
              {sorted.map((c) => (
                <tr
                  key={c.id}
                  className={`group cursor-pointer border-t border-ele-purple-accent transition hover:bg-ele-surface ${c.renewalDueSoon ? "bg-ele-inset" : ""}`}
                  onClick={(e) => {
                    if ((e.target as HTMLElement).closest("a,button,input,select,textarea,label")) return;
                    navigate(`/office/contracts/${c.id}`);
                  }}
                >
                  <td className="px-3 py-2 align-top text-ele-strong">
                    <Link to={`/office/contracts/${c.id}`} className="hover:text-ele-green hover:underline">{c.customerName}</Link>
                  </td>
                  <td className="px-3 py-2 align-top max-md:hidden">
                    <div className="truncate">{c.siteName}</div>
                    {/* Session ARY, Item 4: only in the Xero-billing filtered view, so the column
                        reads exactly as before everywhere else. */}
                    {xeroBillingOnly && c.xeroBilling && (
                      <div className={`mt-0.5 truncate text-xs ${c.xeroBilling.severity === "warning" ? "text-amber-400" : "text-ele-body opacity-70"}`}>
                        {c.xeroBilling.headline}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 align-top max-md:hidden">{TIER_LABEL[c.tier] ?? c.tier}</td>
                  <td className="px-3 py-2 text-right align-top text-ele-strong">{gbp(c.annualValue)}</td>
                  <td className="px-3 py-2 align-top max-md:hidden">{FREQ_LABEL[c.billingFrequency] ?? c.billingFrequency}</td>
                  <td className="px-3 py-2 align-top">
                    <StatusPill domain="contract" status={c.status} />
                  </td>
                  <td className="px-3 py-2 align-top max-md:hidden">
                    {/* The 'Due soon' flag sits on its own line so it is never clipped by the fixed
                        table layout (Session AJ Item 4 review). A not-yet-commenced (pending
                        signature or draft) contract has no renewal date yet, so show a plain-English
                        note instead of a bare empty-value glyph (Session AR Item 5). */}
                    {c.renewalDate ? (
                      <div className="truncate">{formatDate(c.renewalDate)}</div>
                    ) : c.status === "pending_signature" || c.status === "draft" ? (
                      // truncate to match the date branch above (Session AEN): this string measures
                      // 156px of ink against a 93px cell, so it has never fitted at any width this
                      // column has had, and without it the text is cut off at the cell edge with no
                      // ellipsis to say so (text-overflow on the td does not reach a child block).
                      <div className="truncate text-xs text-ele-body opacity-70">Pending commencement</div>
                    ) : (
                      <div className="truncate">-</div>
                    )}
                    {c.renewalDueSoon && (
                      <div className="mt-1">
                        <StatusPill word="Due soon" tone="amber" variant="solid" />
                      </div>
                    )}
                    {isRenewalOverdue(c) && (
                      <div className="mt-1">
                        <StatusPill word="Overdue" tone="red" />
                      </div>
                    )}
                  </td>
                  {/* escalatorPct is the operational uplift over CPI (Session ADU): "CPI + X%". */}
                  <td className="px-3 py-2 text-right align-top max-md:hidden">{c.escalatorPct == null ? "-" : `CPI + ${c.escalatorPct}%`}</td>
                  <td className="cell-actions px-3 py-2 text-right align-top max-md:hidden">
                    {/* flex-wrap is a guard, not a cosmetic (Session AEN): the column above is
                        sized for both buttons on one line at every normal office width, and if a
                        narrower window ever squeezes it, the pair drops onto two right-aligned
                        lines instead of spilling sideways over the Escalator column. */}
                    <div className="flex flex-wrap justify-end gap-3">
                      {/* Session BBJ: the renewal funnel's front door. Active contracts only (a contract that has not
                          started, or has ended, has nothing to renew); the Renewal tab it opens confirms the lifts first
                          and applies every renewal rule. */}
                      {c.status === "active" && (
                        <Link to={`/office/contracts/${c.id}?tab=renewal`} className="text-ele-green hover:underline">
                          Start renewal
                        </Link>
                      )}
                      <button onClick={() => setRenewFor(c)} className="text-ele-green hover:underline">
                        Generate
                      </button>
                      <button
                        onClick={() => {
                          setDeleteError(null);
                          setDeleteFor(c);
                        }}
                        className="text-ele-danger hover:underline"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                  <td className="cell-actions md:hidden pr-1 text-right">
                    <svg aria-hidden viewBox="0 0 16 16" className="ml-auto h-4 w-4 fill-current text-ele-purple transition-colors group-hover:text-ele-green group-focus-within:text-ele-green">
                      <path d="M6 4l4 4-4 4z" />
                    </svg>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Session SC2C: 50 at a time; the total is the server's count of every contract matching the view. */}
      {!loading && !error && (
        <div className={page.refreshing ? "opacity-60" : undefined}>
          <Pager offset={page.offset} total={total} shown={shown.length} pageSize={CONTRACT_PAGE_SIZE} onOffset={page.setOffset} noun={["contract", "contracts"]} />
        </div>
      )}

      {renewFor && <RenewalModal contract={renewFor} onClose={() => setRenewFor(null)} />}

      {deleteFor && (
        <ConfirmDeleteModal
          open={deleteFor !== null}
          title="Delete contract?"
          message={`Deleting the contract for ${deleteFor.siteName} (${deleteFor.customerName}) removes it permanently. It is blocked if the contract is active or has invoices, jobs or LOLER reports on file. This cannot be undone.`}
          confirmLabel="Delete contract"
          busy={deleteBusy}
          error={deleteError}
          onConfirm={confirmDelete}
          onCancel={() => {
            setDeleteFor(null);
            setDeleteError(null);
          }}
        />
      )}
    </div>
  );
}

// The renewal-letter draft/edit/save-to-Gmail modal. Exported so the Renewals page
// reuses it -- it only needs the contract's id, site name and tier.
export function RenewalModal({ contract, onClose }: { contract: { id: string; siteName: string; tier: string }; onClose: () => void }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<RenewalDraft | null>(null);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setError(null);
    api.contracts
      .renewalLetter(contract.id)
      .then((d) => {
        if (!live) return;
        setDraft(d);
        setSubject(d.subject);
        setBody(d.body);
      })
      // Session APB: a refusal the server explains in a sentence (a cycle that has already renewed, whose
      // letter would offer a link the customer's page refuses) is shown as given, not as a generic failure.
      .catch((err) => {
        if (!live) return;
        setError(
          err instanceof ApiError && typeof err.code === "string" && err.code.includes(" ")
            ? err.code
            : "Could not draft the renewal letter. Please try again.",
        );
      })
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [contract.id]);

  async function sendToGmail() {
    setSending(true);
    setError(null);
    try {
      await api.contracts.sendRenewal(contract.id, { subject, body });
      setSent(true);
    } catch (err) {
      setError(friendlyApiError(err, "Could not create the Gmail draft."));
    } finally {
      setSending(false);
    }
  }

  async function downloadPdf() {
    setPdfBusy(true);
    setError(null);
    try {
      await api.contracts.renewalLetterPdf(contract.id, { subject, body });
    } catch (err) {
      setError(err instanceof ApiError ? err.message || err.code : "Could not generate the PDF.");
    } finally {
      setPdfBusy(false);
    }
  }

  return (
    <ModalShell
      ariaLabel={`Renewal letter for ${contract.siteName}`}
      onClose={() => !sending && !pdfBusy && onClose()}
      width="max-w-[560px]"
      panelClassName="max-h-[90vh] overflow-y-auto p-6"
    >
        <div className="mb-3 flex items-start justify-between gap-3">
          <h2 className="font-heading text-xl text-ele-blue">Renewal letter: {contract.siteName}</h2>
          <button onClick={onClose} aria-label="Close" className="text-ele-body hover:text-ele-green">✕</button>
        </div>

        {loading ? (
          <p className="text-sm text-ele-body">Drafting...</p>
        ) : (
          <>
            {draft && (
              <p className="mb-3 text-xs text-ele-body opacity-70">
                {draft.source === "ai" ? "AI-drafted" : "Templated draft (AI not configured)"}. Review and edit before sending.
                {" "}Current {TIER_LABEL[contract.tier] ?? contract.tier}: {gbp(draft.currentAnnual)} plus VAT
                {draft.newAnnual != null ? ` -> ${gbp(draft.newAnnual)} plus VAT${draft.indicative ? " (indicative)" : ""}` : ""}.
              </p>
            )}
            <label className="mb-1 block text-sm" htmlFor="rl-subject">Subject</label>
            <input
              id="rl-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="mb-3 w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 outline-none focus:border-ele-green"
            />
            <label className="mb-1 block text-sm" htmlFor="rl-body">Letter</label>
            <textarea
              id="rl-body"
              rows={14}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm outline-none focus:border-ele-green"
            />

            {error && (
              <p role="alert" className="mt-3 rounded-ele border border-ele-purple-accent bg-ele-surface px-3 py-2 text-sm text-ele-strong">{error}</p>
            )}
            <div className="mt-4">
              <button
                onClick={downloadPdf}
                disabled={pdfBusy || !body.trim()}
                className="rounded-ele border border-ele-blue px-4 py-2 font-heading text-sm tracking-wide text-ele-blue disabled:opacity-50"
              >
                {pdfBusy ? "Preparing PDF..." : "Download PDF"}
              </button>
            </div>
            {sent ? (
              <p role="status" className="mt-3 rounded-ele border border-ele-green bg-ele-surface px-3 py-2 text-sm text-ele-green">
                Saved to Gmail drafts. Review and send it from Gmail.
              </p>
            ) : (
              <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                <Button onClick={sendToGmail} disabled={sending} className={MODAL_ACTION_BTN}>
                  {sending ? "Saving..." : "Send to Gmail drafts"}
                </Button>
                <button onClick={onClose} className={`rounded-ele border border-ele-purple-accent px-4 py-2 text-sm ${MODAL_ACTION_BTN}`}>Cancel</button>
              </div>
            )}
          </>
        )}
    </ModalShell>
  );
}
